import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import Button from '@/components/Button';
import Dialog, {
	DialogContent,
	DialogFooter,
	DialogTop
} from '@/components/Dialog';
import {
	DropdownMenuItem,
	DropdownMenuSeparator
} from '@/components/DropdownMenu';
import Input from '@/components/form/Input';
import Remove from '@/icons/delete-folder.svg';
import { deleteFolder, updateFolder } from '@/store/myDocuments/thunks';
import { showError, showSuccess } from '@/utils';

export function EditFolderName({ item, open, onOpenChange }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isUpdateFolderLoading = useSelector(
		state => state.myDocuments.updateFolder.loading
	);
	const INPUTS = {
		FOLDER_NAME: 'folderName'
	};
	const schema = Yup.object({
		[INPUTS.FOLDER_NAME]: Yup.string()
			.max(100)
			.required()
			.notOneOf(
				[item.name],
				t('myDocuments.modals.editFolderName.validateNotOneOf')
			)
	});
	const methods = useForm({
		resolver: yupResolver(schema),
		defaultValues: { [INPUTS.FOLDER_NAME]: '' },
		mode: 'onChange'
	});

	const handleEdit = async values => {
		const name = values[INPUTS.FOLDER_NAME];

		dispatch(updateFolder({ folderId: item.id, name }))
			.unwrap()
			.then(() => {
				onOpenChange(false);
				showSuccess(t('myDocuments.toast.editFolderNameSuccess'));
			})
			.catch(error => {
				showError(error);
			});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop
					title={t('myDocuments.modals.editFolderName.title')}
					description={t('myDocuments.modals.editFolderName.description')}
				/>

				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(handleEdit)}>
						<div className="flex flex-col px-4 pb-9 pt-9">
							<Input
								name="folderName"
								label={t('myDocuments.modals.editFolderName.label')}
								placeholder={item.name}
								size="lg"
							/>
						</div>
						<DialogFooter>
							<Button
								onClick={() => onOpenChange(false)}
								variant="outline"
							>
								{t('common.cancel')}
							</Button>
							<Button loading={isUpdateFolderLoading} type="submit">
								{t('common.save')}
							</Button>
						</DialogFooter>
					</form>
				</FormProvider>
			</DialogContent>
		</Dialog>
	);
}

export function RemoveFolder({ item, open, onOpenChange }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const isRemoveFolderLoading = useSelector(
		state => state.myDocuments.deleteFolder.loading
	);

	const handleRemoveFolder = async () => {
		dispatch(deleteFolder({ folderId: item.id }))
			.unwrap()
			.then(() => {
				onOpenChange(false);
				showSuccess(t('myDocuments.toast.removeFolderSuccess'));
			})
			.catch(error => {
				showError(error);
			});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="w-[280px] md:w-[397px]">
				<DialogTop
					title={t('myDocuments.modals.removeFolder.title')}
					description={t('myDocuments.modals.removeFolder.description')}
				/>

				<div className="flex flex-col items-center gap-2 px-4 py-9">
					<Remove />
					<p className="whitespace-pre-line text-center text-sm text-grey-500">
						{t('myDocuments.modals.removeFolder.text')}
					</p>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button
						loading={isRemoveFolderLoading}
						onClick={handleRemoveFolder}
					>
						{t('common.delete')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function DialogItem({ options, setOpenOption }) {
	return (
		<>
			{options.map(({ Icon, label, name }) => {
				const handleClick = () => {
					setOpenOption(name);
				};

				return (
					<div key={name}>
						{name === 'delete' && <DropdownMenuSeparator />}
						<DropdownMenuItem onClick={handleClick}>
							<Icon />
							{label}
						</DropdownMenuItem>
					</div>
				);
			})}
		</>
	);
}
